import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CallCard from "../components/CallCard";
import ScoreChart from "../components/ScoreChart";
import { useLocale } from "../contexts/LocaleContext";
import {
  getMemberCalls,
  getMemberMetrics,
  getUserProfile,
} from "../utils/firebase";

const PAGE_SIZE = 20;

export default function MemberDetailPage() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { localizePath } = useLocale();

  const [authorized, setAuthorized] = useState(null);
  const [calls, setCalls] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({
    email: null,
    avgScore: null,
    totalCompleted: 0,
    latestScore: null,
  });

  const loadMetrics = useCallback(async () => {
    try {
      const result = await getMemberMetrics({ memberId });
      setMetrics(result.data);
    } catch (err) {
      console.error("Failed to load member metrics:", err);
      setError(err.message || t("memberDetailPage.failedLoad"));
    }
  }, [memberId, t]);

  const loadCalls = useCallback(async () => {
    try {
      const result = await getMemberCalls({ memberId, limit: PAGE_SIZE });
      setCalls(result.data.calls || []);
      setHasMore(result.data.hasMore || false);
    } catch (err) {
      console.error("Failed to load member calls:", err);
      setError(err.message || t("memberDetailPage.failedLoad"));
    } finally {
      setLoading(false);
    }
  }, [memberId, t]);

  useEffect(() => {
    let cancelled = false;
    getUserProfile()
      .then((r) => {
        if (cancelled) return;
        const { teamRole } = r.data || {};
        if (teamRole !== "admin") {
          setAuthorized(false);
          navigate(localizePath("/"), { replace: true });
          return;
        }
        setAuthorized(true);
        loadMetrics();
        loadCalls();
      })
      .catch(() => {
        if (!cancelled) {
          setAuthorized(false);
          navigate(localizePath("/"), { replace: true });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, localizePath, loadMetrics, loadCalls]);

  const loadMoreCalls = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const lastTimestamp = calls[calls.length - 1]?.timestamp;
      const result = await getMemberCalls({
        memberId,
        limit: PAGE_SIZE,
        before: lastTimestamp,
      });
      const newCalls = result.data.calls || [];
      setCalls((prev) => [...prev, ...newCalls]);
      setHasMore(result.data.hasMore || false);
    } catch (err) {
      console.error("Failed to load more calls:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  if (authorized === null || (authorized && loading)) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (!authorized) {
    return null;
  }

  const completedCalls = calls.filter(
    (c) => c.status === "completed" && c.score != null
  );
  const chartCalls = [...completedCalls].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );
  const chartData = {
    labels: chartCalls.map((c) => {
      const d = new Date(c.timestamp);
      return d.toLocaleDateString("en-US", { day: "numeric", month: "short" });
    }),
    data: chartCalls.map((c) => c.score),
  };

  const MetricCard = ({ value, label }) => (
    <Box
      sx={{
        p: 3,
        border: "1px solid #e5e7eb",
        borderRadius: 2,
        bgcolor: "#ffffff",
        textAlign: "center",
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Cal Sans", "Inter", sans-serif',
          fontSize: 36,
          fontWeight: 600,
          letterSpacing: "-0.02em",
          color: "#111827",
        }}
      >
        {value ?? "—"}
      </Typography>
      <Typography sx={{ color: "#6b7280", fontSize: 14, mt: 0.5 }}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 5, px: { xs: 2, sm: 3 } }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(localizePath("/team"))}
          sx={{ textTransform: "none", color: "#6b7280", mb: 2 }}
        >
          {t("memberDetailPage.backToTeam")}
        </Button>

        <Box mb={4}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Cal Sans", "Inter", sans-serif',
              fontWeight: 600,
              letterSpacing: "-0.02em",
              color: "#111827",
              fontSize: 28,
            }}
          >
            {metrics.email || t("memberDetailPage.callsAndScores")}
          </Typography>
          {metrics.email && (
            <Typography sx={{ color: "#6b7280", fontSize: 14, mt: 0.5 }}>
              {t("memberDetailPage.callsAndScores")}
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <MetricCard
                value={metrics.avgScore}
                label={t("memberDetailPage.averageScore")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MetricCard
                value={metrics.totalCompleted}
                label={t("memberDetailPage.analyzedCalls")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MetricCard
                value={metrics.latestScore}
                label={t("memberDetailPage.latestScore")}
              />
            </Grid>
          </Grid>
        </Box>

        {chartData.data.length > 0 && (
          <ScoreChart
            labels={chartData.labels}
            data={chartData.data}
            title={t("memberDetailPage.scoreTrend")}
          />
        )}

        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Cal Sans", "Inter", sans-serif',
            fontWeight: 600,
            letterSpacing: "0.02em",
            color: "#111827",
            mb: 2,
            fontSize: 18,
          }}
        >
          {t("memberDetailPage.callHistory")}
        </Typography>

        {calls.length === 0 ? (
          <Box
            sx={{
              p: 6,
              textAlign: "center",
              border: "1px solid #e5e7eb",
              borderRadius: 2,
            }}
          >
            <Typography sx={{ color: "#9ca3af", fontSize: 14 }}>
              {t("memberDetailPage.noCalls")}
            </Typography>
          </Box>
        ) : (
          <>
            {calls.map((call) => (
              <CallCard
                key={call.id}
                call={call}
                canManageMeetings={false}
                memberId={memberId}
              />
            ))}
            {hasMore && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Button
                  variant="outlined"
                  onClick={loadMoreCalls}
                  disabled={loadingMore}
                  sx={{ textTransform: "none", minWidth: 160 }}
                >
                  {loadingMore ? (
                    <CircularProgress size={20} />
                  ) : (
                    t("memberDetailPage.loadMore")
                  )}
                </Button>
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}
